import { generatePath } from 'react-router-dom';
import { UseMutationOptions, UseQueryOptions, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '../../interceptor/axiosInstance';
import { ApiRoutes } from '../../routes/routeConstants/apiRoutes';
import { QueryKeys } from '../../enums/queryKeys';
import { MutationKeys } from '../../enums/mutationKeys';
import { LocalStorageKeys } from '../../enums/localStorageKeys';
import { useAuth } from '../../context/AuthContext';
import Notification from '../../shared/components/Notification';
import { NotificationTypes } from '../../enums/notificationTypes';
import { getApiErrorMessage } from '../../shared/utils/apiErrorMessage';
import { selectClientServiceMessages } from './selectClient.service.constants';

export interface SelectableClient {
  id: number;
  name: string;
  organizationId: number;
  organizationName: string;
}

interface ClientsForSelectionResponse {
  clients: SelectableClient[];
}

interface ViewAsClientResponse {
  accessToken: string;
  actingAs: { clientId: number; clientName: string };
}

const SelectClientService = () => {
  const { setSelectedClient } = useAuth();
  const queryClient = useQueryClient();

  const getClients = (suppressErrorNotification = false): UseQueryOptions<SelectableClient[], Error> => ({
    queryKey: [QueryKeys.GET_CLIENTS_FOR_SELECTION],
    queryFn: async () => {
      try {
        const { data } = await axiosInstance.get<ClientsForSelectionResponse>(ApiRoutes.CLIENTS_FOR_SELECTION);
        return data.clients;
      } catch (error) {
        if (!suppressErrorNotification) {
          Notification({
            message: selectClientServiceMessages.GET_ERROR,
            description: getApiErrorMessage(error, error instanceof Error ? error.message : '') || undefined,
            type: NotificationTypes.ERROR,
          });
        }
        throw error;
      }
    },
  });

  const viewAsClient = (suppressErrorNotification = false): UseMutationOptions<ViewAsClientResponse, Error, SelectableClient> => ({
    mutationKey: [MutationKeys.VIEW_AS_CLIENT],
    mutationFn: async (client) => {
      const { data } = await axiosInstance.post<ViewAsClientResponse>(generatePath(ApiRoutes.CLIENT_VIEW_AS, { clientId: String(client.id) }));
      // Every subsequent request must be scoped to this client — axiosInstance reads
      // the token fresh per request, so overwriting it here is enough (no reload needed).
      localStorage.setItem(LocalStorageKeys.AUTH_TOKEN, data.accessToken);
      setSelectedClient({ id: data.actingAs.clientId, name: data.actingAs.clientName });
      // Drop anything cached under the CES Admin's own (unscoped) token so the newly
      // scoped views don't briefly show stale cross-client data.
      queryClient.clear();
      return data;
    },
    onError: (error) => {
      if (!suppressErrorNotification) {
        Notification({
          message: selectClientServiceMessages.VIEW_AS_ERROR,
          description: getApiErrorMessage(error, error instanceof Error ? error.message : '') || undefined,
          type: NotificationTypes.ERROR,
        });
      }
    },
  });

  return { getClients, viewAsClient };
};

export default SelectClientService;
